import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { DateRange } from "@/hooks/useDateRange";
import { useRegisterAnalyticsTabExport } from "@/components/pages/analytics/front-desk/context/AnalyticsTabExportContext";
import { toAnalyticsDateParam } from "@/components/pages/analytics/front-desk/utils/analyticsDateUtils";
import { downloadBlob } from "@/utils/downloadFile";
import { analyticsTenantService } from "@/services/analytics-tenant-service";
import type { TenantExportTab } from "@/types/analytics-tenant";

type UseAdminTenantTableExportParams = {
  tab: TenantExportTab;
  dateRange: DateRange;
  canExport: boolean;
};

export function useAdminTenantTableExport({
  tab,
  dateRange,
  canExport,
}: UseAdminTenantTableExportParams): void {
  const { t } = useTranslation();

  const handleExport = useCallback(
    async (format: "csv" | "xlsx") => {
      const from = toAnalyticsDateParam(dateRange.from);
      const to = toAnalyticsDateParam(dateRange.to);

      if (!from || !to || !canExport) {
        return;
      }

      try {
        const blob = await analyticsTenantService.exportTable(tab, {
          from,
          to,
          format,
        });
        downloadBlob(blob, `tenant-${tab}-${from}-${to}.${format}`);
        toast.success(
          t("analytics.admin.export.success", "Analytics export downloaded"),
        );
      } catch {
        toast.error(
          t("analytics.admin.export.error", "Failed to export analytics"),
        );
      }
    },
    [canExport, dateRange.from, dateRange.to, tab, t],
  );

  const exportRegistration = useMemo(
    () => ({
      canExport,
      onExport: handleExport,
    }),
    [canExport, handleExport],
  );

  useRegisterAnalyticsTabExport(exportRegistration);
}
